import * as React from 'react';
import type { AiLineItem, BravoWriteResult } from 'shared/src/api/ai-line-item-chat-types';
import { trpc } from '@/utils/trpc-client';
import { showErrorToast } from '@/components/feedback/error-toast';
import { CommitCardFrame, type CommitOutcome } from '@/features/ai/shared/commit-card-frame';

type LineItemPreviewCardProps = {
  lineItems: AiLineItem[];
  proposalId: string;
  proposalPrice: number;
  /** The createLineItems tool call this card confirms. */
  toolCallId: string;
  /** Outcome of a resolution made outside this card (prior session, or the modal's soft-cancel). */
  externalOutcome?: CommitOutcome | null;
  platformNameById?: Record<number, string>;
  /** Always called on every terminal outcome (created / cancelled / failed). */
  onResolve: (output: BravoWriteResult) => void | Promise<void>;
};

export function LineItemPreviewCard({
  lineItems,
  proposalId,
  proposalPrice,
  toolCallId,
  externalOutcome,
  platformNameById,
  onResolve
}: LineItemPreviewCardProps) {
  const utils = trpc.useUtils();
  const [outcome, setOutcome] = React.useState<CommitOutcome | null>(null);

  const { mutateAsync, isPending } = trpc.bravoCreateLineItems.useMutation();

  async function handleCreate() {
    try {
      const res = await mutateAsync({
        proposal_id: proposalId,
        tool_call_id: toolCallId,
        line_items: lineItems
      });
      setOutcome({
        status: 'applied',
        summary: `${res.created_count} line item${res.created_count === 1 ? '' : 's'} created`
      });
      await utils.getProposalLineItems.invalidate();
      await utils.campaignV2.invalidate();
      await utils.proposalDiff.invalidate();
      // Always resolve the tool so the agent resumes.
      await onResolve({ status: 'created', created_count: res.created_count });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create line items';
      setOutcome({ status: 'failed', summary: message });
      showErrorToast(message);
      await onResolve({ status: 'failed', error: message });
    }
  }

  async function handleCancel() {
    setOutcome({ status: 'cancelled' });
    await onResolve({ status: 'cancelled' });
  }

  const totalPrice = lineItems.reduce((sum, li) => sum + li.price, 0);
  const priceDiff = Math.abs(totalPrice - proposalPrice);
  const priceMatch = priceDiff <= 0.01;

  return (
    <CommitCardFrame
      tier="staged"
      title={`Create ${lineItems.length} line item${lineItems.length > 1 ? 's' : ''}`}
      consequence="Creates these line items on this proposal — nothing goes live until you promote."
      confirmLabel="Create in Bravo"
      isPending={isPending}
      outcome={outcome}
      externalOutcome={externalOutcome ?? null}
      toolCallId={toolCallId}
      onConfirm={handleCreate}
      onCancel={handleCancel}>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead>
            <tr className="border-b text-gray-500">
              <th className="pb-2 pr-3 font-medium">Name</th>
              <th className="pb-2 pr-3 font-medium">Channel</th>
              <th className="pb-2 pr-3 font-medium">Tactic</th>
              <th className="pb-2 pr-3 font-medium">Unit Price Type</th>
              <th className="pb-2 pr-3 font-medium">Platforms</th>
              <th className="pb-2 pr-3 text-right font-medium">Price</th>
              <th className="pb-2 pr-3 text-right font-medium">Unit Price</th>
              <th className="pb-2 pr-3 text-right font-medium">Planned Unit Cost</th>
              <th className="pb-2 pr-3 font-medium">Dates</th>
              <th className="pb-2 font-medium">Geo</th>
            </tr>
          </thead>
          <tbody>
            {lineItems.map((li, i) => (
              <tr key={i} className="border-b last:border-b-0">
                <td className="py-2 pr-3">{li.name}</td>
                <td className="py-2 pr-3">{li.channel_name}</td>
                <td className="py-2 pr-3">{li.tactic_name}</td>
                <td className="py-2 pr-3">{li.unit_price_type_name}</td>
                <td className="py-2 pr-3">
                  <PlatformChips ids={li.media_platform_ids ?? []} nameById={platformNameById} />
                </td>
                <td className="py-2 pr-3 text-right">${li.price.toLocaleString()}</td>
                <td className="py-2 pr-3 text-right">${li.unit_price}</td>
                <td className="py-2 pr-3 text-right">${li.planned_unit_cost}</td>
                <td className="py-2 pr-3 whitespace-nowrap">
                  {li.date_from} – {li.date_to}
                </td>
                <td className="py-2">{li.geo}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3 border-t pt-3 text-sm">
        <span className="font-medium">Total:</span>{' '}
        <span className={priceMatch ? 'text-green-600' : 'text-red-600'}>
          ${totalPrice.toLocaleString()}
        </span>
        {' / '}
        <span className="text-gray-500">Budget: ${proposalPrice.toLocaleString()}</span>
        {!priceMatch ? (
          <span className="ml-2 text-xs text-red-500">(off by ${priceDiff.toFixed(2)})</span>
        ) : null}
      </div>
    </CommitCardFrame>
  );
}

function PlatformChips({ ids, nameById }: { ids: number[]; nameById?: Record<number, string> }) {
  if (ids.length === 0) {
    return <span className="text-xs text-gray-400">—</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {ids.map(id => (
        <span
          key={id}
          className="inline-flex items-center rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">
          {nameById?.[id] ?? `Platform ${id}`}
        </span>
      ))}
    </div>
  );
}
